import { AddOutlined } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';

import OrganizationSelector from '../../profile/components/organizationSelector';
import OrganizationAgentForm from '../components/organizationAgentForm';
import OrganizationAgentsTable from '../components/organizationAgentsTable';
import PageHeader from 'components/v2/common/pageHeader';

import api from 'api';
import { ACTIONS } from 'constant';
import useModalObj from 'hooks/useModalObj';
import { organizationAgentKeys, useOrganizationAgents } from 'hooks/useOrganizationAgents';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import { OrganizationAgentFormInterface } from 'types';
import { getErrorMessage } from 'utils/v2/errorHelper';

const OrganizationAgents = () => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const { translate } = useTranslate();

    const { modalObject, setModalObject, onResetModalObj } = useModalObj();
    const { data: agents = [], isLoading } = useOrganizationAgents(id);
    const [pendingInviteAgentId, setPendingInviteAgentId] = useState<number | null>(null);

    const handleAction = (action: ACTIONS) => {
        switch (action) {
            case ACTIONS.CREATE:
                setModalObject((prev) => ({
                    ...prev,
                    action,
                    title: 'createAgent',
                    data: null,
                }));
                break;
        }
    };

    const handleSubmit = async (value: OrganizationAgentFormInterface) => {
        if (!id) return;
        try {
            setModalObject((prev) => ({ ...prev, disabled: true }));
            await api.clientServiceProviders.createOrganizationAgent(id, value);
            dispatch(
                setAlert({
                    message: 'agentCreated',
                    type: 'success',
                })
            );
            await queryClient.invalidateQueries({
                queryKey: organizationAgentKeys.byServiceProvider(id),
            });
            onResetModalObj();
        } catch (error: unknown) {
            setModalObject((prev) => ({ ...prev, disabled: false }));
            dispatch(
                setAlert({
                    message: getErrorMessage(error, 'somethingWentWrong'),
                    type: 'error',
                })
            );
        }
    };

    const handleResendInvitation = async (agentId: number) => {
        try {
            setPendingInviteAgentId(agentId);
            const response = await api.sendInvitation.sendInvitation(agentId);
            if (response.status === 200 || response.status === 201) {
                dispatch(setAlert({ message: 'invitationSuccessfullySent', type: 'success' }));
            }
        } catch {
            dispatch(setAlert({ message: 'somethingWentWrong', type: 'error' }));
        } finally {
            setPendingInviteAgentId(null);
        }
    };

    return (
        <>
            <Box sx={{ width: '100%', px: { xs: 2, md: 0 } }}>
                <PageHeader title="agents">
                    <OrganizationSelector />
                    <Button
                        size="small"
                        variant="contained"
                        startIcon={<AddOutlined />}
                        onClick={() => handleAction(ACTIONS.CREATE)}
                    >
                        {translate('createAgent')}
                    </Button>
                </PageHeader>
                <OrganizationAgentsTable
                    agents={agents}
                    loading={isLoading}
                    pendingInviteAgentId={pendingInviteAgentId}
                    onResendInvitation={handleResendInvitation}
                />
            </Box>

            {modalObject.action === ACTIONS.CREATE && (
                <OrganizationAgentForm
                    disabled={modalObject.disabled}
                    title={modalObject.title}
                    onSubmit={handleSubmit}
                    onClose={onResetModalObj}
                />
            )}
        </>
    );
};

export default OrganizationAgents;
